// src/pages/PaidInOutScreen.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Backdrop,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Paper,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Container,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import Footer from 'components/Footer';
import {
  finishCashSaleTransaction,
  listTransactionTypes,
  startNonSaleTransaction,
} from 'api';
import {
  CashSaleCompletionRequest,
  ListTransactionTypesResponse,
  NonSaleTransactionRequest,
} from 'api/types';
import { useCurrencyInput } from 'hooks/useCurrencyInput';
import type { SnackbarCloseReason } from '@mui/material/Snackbar';

type BalanceModification = 'cash_in' | 'cash_out';
interface Reason {
  code: string;
  label: string;
}
interface TransactionInfo {
  key: string;
  label: string;
  balanceModification: BalanceModification;
  reasons: Reason[];
}

const PaidInOut: React.FC = () => {
  const theme = useTheme();
  const isTabletUp = useMediaQuery(theme.breakpoints.up('sm'));
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cashInTransactions, setCashInTransactions] = useState<TransactionInfo[]>([]);
  const [cashOutTransactions, setCashOutTransactions] = useState<TransactionInfo[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionInfo | null>(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [errorToastOpen, setErrorToastOpen] = useState(false);
  const [successToastOpen, setSuccessToastOpen] = useState(false);
  const navigate = useNavigate();

  const transaction = useCurrencyInput();

  useEffect(() => {
    const fetchTransactionTypes = async () => {
      try {
        const data: ListTransactionTypesResponse = await listTransactionTypes();
        const types = data.transaction_types;
        const inList: TransactionInfo[] = [];
        const outList: TransactionInfo[] = [];

        Object.entries(types).forEach(([key, val]) => {
          if (val.status !== 'configured' || !val.configuration) return;
          if (key === 'transfer_in' || key === 'transfer_out') return;

          const { balance_modification, reasons = [] } = val.configuration;
          const balanceModification = balance_modification as BalanceModification;

          let label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          if (key === 'adjustment_in') label = 'Paid In';
          else if (key === 'adjustment_out') label = 'Paid Out';

          const info: TransactionInfo = {
            key,
            label,
            balanceModification,
            reasons: reasons as Reason[],
          };
          if (balanceModification === 'cash_in') inList.push(info);
          else outList.push(info);
        });

        setCashInTransactions(inList);
        setCashOutTransactions(outList);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(err);
        setErrorMessage(`Failed to load transaction types. ${msg}`);
        setErrorToastOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactionTypes();
  }, []);

  const handleSubmit = async () => {
    if (!selectedTransaction) return;
    setLoading(true);
    try {
      const amt = transaction.numericValue;
      const createTransactionPayload: NonSaleTransactionRequest = {
        transaction_type: selectedTransaction.key,
        reason_code: selectedReason,
        ...(selectedTransaction.balanceModification === 'cash_in'
          ? { amount_in: { value: amt, currency: 'USD' } }
          : { amount_out: { value: amt, currency: 'USD' } }),
      };

      const created = await startNonSaleTransaction(createTransactionPayload);
      const completionRequest: CashSaleCompletionRequest = { status: 'completed' };
      await finishCashSaleTransaction(created.transaction_id, completionRequest);

      setSuccessToastOpen(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error(err);
      setErrorMessage(`Failed to submit transaction. ${msg}`);
      setErrorToastOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Snackbar handlers (typed to avoid TS/ESLint issues)
  const handleSuccessSnackbarClose = (
    _event: Event | React.SyntheticEvent<Element, Event>,
    reason: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') return;
    setSuccessToastOpen(false);
    navigate(-1);
  };
  const handleSuccessAlertClose = (_event: React.SyntheticEvent<Element, Event>) => {
    console.log(_event)
    setSuccessToastOpen(false);
    navigate(-1);
  };
  const handleErrorSnackbarClose = (
    _event: Event | React.SyntheticEvent<Element, Event>,
    reason: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') return;
    setErrorToastOpen(false);
  };
  const handleErrorAlertClose = (_event: React.SyntheticEvent<Element, Event>) => {
    console.log(_event)
    setErrorToastOpen(false);
  };

  const AmountInput = (
    <TextField
      fullWidth
      type="text"
      placeholder="Enter amount"
      inputRef={transaction.inputRef}
      value={transaction.formattedValue}
      onKeyDown={transaction.onKeyDown}
      onFocus={transaction.onFocus}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      }}
      sx={{
        mt: 2,
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.6),
          transition: 'box-shadow .2s ease',
          '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
        },
        '& .MuiInputBase-input': {
          textAlign: 'center',
          fontSize: isTabletUp ? 24 : 20,
          fontWeight: 700,
          letterSpacing: 0.3,
          caretColor: 'transparent',
        },
      }}
      inputProps={{
        inputMode: 'numeric',
        pattern: '[0-9]*',
        enterKeyHint: 'done',
      }}
    />
  );

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: '16px' }}>
      <Header
        title={<Typography variant="h6">Paid In/Out</Typography>}
        subtitle={selectedTransaction ? selectedTransaction.label : 'Select a transaction type'}
        showBackButton
        onBack={() => {
          if (selectedTransaction) {
            setSelectedTransaction(null);
            setSelectedReason('');
            transaction.setNumber(0);
          } else {
            navigate('/');
          }
        }}
        align="left"
        sticky
        elevateOnScroll
      />



      {/* Loading overlay */}
      <Backdrop sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Panel */}
      <Container
        disableGutters
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 720, md: 880 },
          mx: 'auto',
        }}
      >
        <Paper
          elevation={0}
          component="main"
          sx={{
            mx: 2,
            mt: 2,
            mb: 6,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            background: alpha(theme.palette.primary.light, 0.05),
            backdropFilter: 'saturate(120%) blur(4px)',
          }}
        >
          {errorMessage && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {selectedTransaction ? (
            <Box>
              {/* Selected transaction pill */}
              <Chip
                color={selectedTransaction.balanceModification === 'cash_in' ? 'success' : 'warning'}
                variant="outlined"
                label={selectedTransaction.label}
                sx={{ borderRadius: 2, mb: 2 }}
              />

              {/* Reason selector */}
              {selectedTransaction.reasons.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Reason</InputLabel>
                  <Select
                    value={selectedReason}
                    label="Reason"
                    onChange={(e) => setSelectedReason(e.target.value)}
                    MenuProps={{ PaperProps: { elevation: 2 } }}
                  >
                    {selectedTransaction.reasons.map((r) => (
                      <MenuItem key={r.code} value={r.code}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Amount */}
              {AmountInput}
            </Box>
          ) : (
            <Box>
              {/* Cash In */}
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Cash In
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.25,
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fit, minmax(180px, 1fr))',
                    sm: 'repeat(auto-fit, minmax(200px, 1fr))',
                  },
                }}
              >
                {cashInTransactions.map((tx) => (
                  <Card
                    key={tx.key}
                    variant="outlined"
                    onClick={() => setSelectedTransaction(tx)}
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <CardContent sx={{ py: 1.25, textAlign: 'center' }}>
                      <Typography variant="body1" fontWeight={700}>
                        {tx.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Requires reason: {tx.reasons.length ? 'Yes' : 'No'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Cash Out */}
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Cash Out
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.25,
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fit, minmax(180px, 1fr))',
                    sm: 'repeat(auto-fit, minmax(200px, 1fr))',
                  },
                }}
              >
                {cashOutTransactions.map((tx) => (
                  <Card
                    key={tx.key}
                    variant="outlined"
                    onClick={() => setSelectedTransaction(tx)}
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <CardContent sx={{ py: 1.25, textAlign: 'center' }}>
                      <Typography variant="body1" fontWeight={700}>
                        {tx.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Requires reason: {tx.reasons.length ? 'Yes' : 'No'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
      {/* Footer actions when a transaction is selected */}
      {selectedTransaction && (
        <Footer
          onSubmit={handleSubmit}

          disabled={
            transaction.numericValue === 0 ||
            (selectedTransaction.reasons.length > 0 && !selectedReason)
          }
        />
      )}

      {/* Success Toast */}
      <Snackbar
        open={successToastOpen}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={handleSuccessSnackbarClose}
        sx={{ mb: '16px' }}
      >
        <Alert onClose={handleSuccessAlertClose} severity="success" sx={{ width: '100%' }}>
          Transaction completed successfully!
        </Alert>
      </Snackbar>

      {/* Error Toast */}
      <Snackbar
        open={errorToastOpen}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={handleErrorSnackbarClose}
        sx={{ mb: '16px' }}
      >
        <Alert onClose={handleErrorAlertClose} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaidInOut;
